import type { FormEvent} from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { useUsers } from "@app/api/users";
import { ErrorMessage, Loading } from "@app/components/Feedback";
import { ROWS_PER_PAGE_OPTIONS } from "@app/constants";

export default function UsersPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data, isLoading, error } = useUsers({ offset: page * rowsPerPage, limit: rowsPerPage, q: q || undefined });

  const onSearch = (event: FormEvent): void => {
    event.preventDefault();
    setQ(searchInput.trim());
    setPage(0);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Users
      </Typography>

      <Stack direction="row" spacing={1} component="form" onSubmit={onSearch} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search email or username"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ width: 320 }}
        />
        <Button type="submit" variant="contained">
          Search
        </Button>
      </Stack>

      {isLoading && <Loading />}
      {error && <ErrorMessage error={error} />}

      {data && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Verified</TableCell>
                <TableCell>Joined</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map((user) => (
                <TableRow key={user.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/users/${user.id}`)}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={user.enabled ? "Enabled" : "Disabled"}
                      color={user.enabled ? "success" : "default"}
                    />
                  </TableCell>
                  <TableCell>{user.verified ? "Yes" : "No"}</TableCell>
                  <TableCell>{new Date(user.createdAt).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
              {data.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No users
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data.total ?? -1}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </TableContainer>
      )}
    </Box>
  );
}
